// hooks.js
import { useEffect, useState, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer"

import { createClusterRenderer } from "Components/chartsLibrary/GMap/markers/clusterRenderer"
import { keyForItem } from "Components/chartsLibrary/GMap/markers/keyForItem"
import { diffMarkerKeys } from "Components/chartsLibrary/GMap/markers/diffMarkerKeys"
import {
  renderInfoWindowContent,
  INFO_WINDOW_BTN_PREFIX,
} from "Components/chartsLibrary/GMap/components/InfoWindowContent"
import { resolveTheme, lightenColor } from "Components/chartsLibrary/GMap/themes"

// Picks the marker accent color based on theme:
// - light → primaryActionColor as-is
// - dark  → lightened by 30% to remain readable on a navy background
const getMarkerColor = (primaryActionColor, theme) => {
  const t = resolveTheme(theme)
  return t === "dark" ? lightenColor(primaryActionColor, 0.3) : primaryActionColor
}

export const useOptions = (
  primaryActionColor,
  lattitudeValueData,
  longitudeValueData,
  googleMapIcon, // kept for API compatibility, ignored visually
  valueData,
  dataNames,
  referentialId,
  useMarkerCluster,
  themeGoogleMap
) => {
  const navigate = useNavigate()

  const [infoWindow, setInfoWindow] = useState(null)
  const [markers, setMarkers] = useState([])
  const [bounds, setBounds] = useState(null)
  const [icons, setIcons] = useState({}) // kept to satisfy initMap signature
  const [map, setMap] = useState(null)

  const markersByKeyRef = useRef(new Map())
  const lastBuildKeyRef = useRef(null)
  const clustererRef = useRef(null)
  const handleMarkerClickRef = useRef(() => {})

  useEffect(() => {
    handleMarkerClickRef.current = (item, mrk) => {
      if (!infoWindow || !map) return
      const theme = resolveTheme(themeGoogleMap)
      const idIndex = dataNames?.findIndex(name => name === "id")
      const idValue = idIndex !== -1 ? item?.[valueData?.[idIndex]] : null

      const html = renderInfoWindowContent({
        valueData,
        dataNames,
        marker: item,
        primaryActionColor,
        idValue,
        theme,
      })

      infoWindow.setContent(html)
      infoWindow.open(map, mrk)

      if (idValue) {
        window.google.maps.event.addListenerOnce(infoWindow, "domready", () => {
          const btn = document.getElementById(`${INFO_WINDOW_BTN_PREFIX}${idValue}`)
          if (btn) {
            btn.addEventListener("click", () => {
              navigate(`/synthesis?idReferential=${referentialId}&idSample=${idValue}`)
            })
          }
        })
      }
    }
  }, [
    infoWindow,
    map,
    themeGoogleMap,
    dataNames,
    valueData,
    primaryActionColor,
    navigate,
    referentialId,
  ])

  const drawMarker = useCallback(() => {
    if (!map) return

    const markerColor = getMarkerColor(primaryActionColor, themeGoogleMap)
    const buildKey = `${markerColor}|${themeGoogleMap}|${useMarkerCluster ? 1 : 0}`
    const buildKeyChanged = lastBuildKeyRef.current !== buildKey
    lastBuildKeyRef.current = buildKey

    const latKey = lattitudeValueData?.[0]?.valueId
    const lngKey = longitudeValueData?.[0]?.valueId

    // Full rebuild path: prop change that affects every marker, OR first run.
    if (buildKeyChanged) {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers()
        clustererRef.current = null
      }
      markersByKeyRef.current.forEach(m => m.setMap(null))
      markersByKeyRef.current = new Map()
    }

    // Compute desired keys (and the corresponding items) from current `markers`.
    const desiredEntries = []
    const desiredKeys = []
    markers.forEach((item, index) => {
      const lat = Number(item?.[latKey])
      const lng = Number(item?.[lngKey])
      if (Number.isNaN(lat) || Number.isNaN(lng)) return
      const key = keyForItem(item, index, {
        valueData,
        dataNames,
        latKey,
        lngKey,
      })
      desiredEntries.push({ key, item, lat, lng })
      desiredKeys.push(key)
    })

    // Diff against current live markers.
    const liveKeys = new Set(markersByKeyRef.current.keys())
    const { added, removed } = diffMarkerKeys(liveKeys, desiredKeys)

    // Remove markers no longer desired.
    if (removed.size > 0) {
      const removedMarkers = []
      removed.forEach(key => {
        const m = markersByKeyRef.current.get(key)
        if (m) {
          m.setMap(null)
          removedMarkers.push(m)
          markersByKeyRef.current.delete(key)
        }
      })
      if (clustererRef.current && removedMarkers.length > 0) {
        clustererRef.current.removeMarkers(removedMarkers)
      }
    }

    // Spec: when the dataset becomes empty, tear down the clusterer rather than
    // keeping an empty instance attached to the map.
    if (markersByKeyRef.current.size === 0 && clustererRef.current) {
      clustererRef.current.clearMarkers()
      clustererRef.current = null
    }

    // Create newly desired markers.
    const newMarkers = []
    if (added.size > 0) {
      desiredEntries.forEach(({ key, item, lat, lng }) => {
        if (!added.has(key)) return
        const mrk = new window.google.maps.Marker({
          position: { lat, lng },
          map: useMarkerCluster ? null : map,
          title: item?.title,
          icon: icons?.[googleMapIcon]?.icon,
          draggable: true,
        })
        mrk.addListener("click", () => handleMarkerClickRef.current(item, mrk))
        markersByKeyRef.current.set(key, mrk)
        newMarkers.push(mrk)
      })
    }

    // Ensure the clusterer exists if enabled, then push new markers into it.
    if (useMarkerCluster) {
      if (!clustererRef.current && markersByKeyRef.current.size > 0) {
        clustererRef.current = new MarkerClusterer({
          map,
          renderer: createClusterRenderer(markerColor),
          algorithm: new SuperClusterAlgorithm({ radius: 80, maxZoom: 17 }),
        })
        // First-time creation: feed all live markers at once.
        clustererRef.current.addMarkers(Array.from(markersByKeyRef.current.values()))
      } else if (clustererRef.current && newMarkers.length > 0) {
        clustererRef.current.addMarkers(newMarkers)
      }
    }
  }, [
    map,
    markers,
    lattitudeValueData,
    longitudeValueData,
    primaryActionColor,
    useMarkerCluster,
    themeGoogleMap,
    valueData,
    dataNames,
    googleMapIcon,
    icons,
  ])

  useEffect(() => {
    drawMarker()
  }, [drawMarker])

  return {
    drawMarker,
    setInfoWindow,
    setMarkers,
    setBounds,
    setIcons,
    setMap,
    infoWindow,
    markers,
    bounds,
    icons,
    map,
  }
}
